import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { customerCommissionSelector, providerCommissionSelector } from '../../../ducks/user.duck';
import { states } from '../../../transactions/transactionProcessBooking';
import css from './OrderDetailsPanel.module.css';
import Button from '../../Button/Button';
import classNames from 'classnames';

const QuoteRequest = ({
  customerQuoteRequest,
  isFinal,
  showQuote,
  setShowQuoteModal,
  proposedQuote
}) => {
  const { destination, travelStyles = [], people, travelDuration, budget, additionalDetails } =
    customerQuoteRequest || {};

  const { deliveryDate = "" } = proposedQuote || {};
  const date = new Date(deliveryDate);
  // Convert to the desired format (e.g., "July 19, 2026")
  const formattedDate = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const data = [
    {
      primaryText: <FormattedMessage id="CustomBookingForm.destination" />,
      secondaryText: <FormattedMessage id="CustomBookingForm.noProblem" />,
      response: destination,
    },
    {
      primaryText: <FormattedMessage id="CustomBookingForm.travelStyleLabel" />,
      secondaryText: null,
      response: travelStyles.join(', '),
    },
    {
      primaryText: <FormattedMessage id="CustomBookingForm.peopleLabel" />,
      secondaryText: null,
      response: people,
    },
    {
      primaryText: <FormattedMessage id="CustomBookingForm.travelDurationLabel" />,
      secondaryText: null,
      response: travelDuration,
    },
    {
      primaryText: <FormattedMessage id="CustomBookingForm.budgetLabel" />,
      secondaryText: null,
      response: budget,
    },
    {
      primaryText: <FormattedMessage id="CustomBookingForm.writeRequest" />,
      secondaryText: <FormattedMessage id="CustomBookingForm.whatWould" />,
      response: additionalDetails,
    },
  ];

  return (
    <div className={css.questionAnswerWrapper}>
      {isFinal ? <>
        <div className={css.dueDate}>Due date: {formattedDate}</div>
        <div className={css.dueDateButton}
          onClick={(e) => {
            e.preventDefault();
            setShowQuoteModal(false);
          }}
        >
          View Summary
        </div>
      </>
        : null}
      <div className={css.questionAnswer}>
        {data.map(({ primaryText, secondaryText, response }, index) => (
          <div key={`${index}-${primaryText}`} className={css.questionAnswerItem}>
            <div>
              <span className={classNames(css.questionText, index === 0 && css.questionTextBold)}>{primaryText}</span>
              <span className={css.answerText}>{secondaryText}</span>
            </div>
            <p className={css.answerTextBold}>{response}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const FinalQuote = ({
  proposedQuote,
  transactionRole,
  transactionState,
  showQuote,
  setShowQuoteModal
}) => {
  const customerCommission = useSelector(customerCommissionSelector);
  const providerCommission = useSelector(providerCommissionSelector);

  const { quoteRate, deliveryDate, estimatedHours, items = [] } = proposedQuote || {};
  const date = new Date(deliveryDate);
  // Convert to the desired format (e.g., "July 19, 2026")
  const formattedDate = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const commissionShow = transactionRole === 'provider' ? providerCommission : providerCommission;
  const formattedRate = quoteRate / 100;
  const commissionRate = commissionShow ? (commissionShow / 100) * formattedRate : 0;
  const formattedCommissionRate = commissionRate % 1 !== 0 ? commissionRate.toFixed(2) : commissionRate;

  const finalRate = transactionRole === 'provider' ? formattedRate - commissionRate : formattedRate + commissionRate;
  const isMobileLayout = window && window.matchMedia(`(max-width: 1024px)`)?.matches;
  const forProviderDeliveredByProvider = transactionState === states.PROVIDER_DELIVERED && transactionRole === 'provider';

  return (
    <div className={css.orderDetails}>
      <div className={css.dueDate}>Due date: {formattedDate}</div>
      <div className={css.dueDateButton}
        onClick={(e) => {
          e.preventDefault();
          setShowQuoteModal(true);
        }}
      >View Request</div>
      <h5 className={css.title}>Order details</h5>
      <div className={css.lineItem}>
        <span className={css.itemLabel}>Estimated hours: </span>{' '}
        <span className={css.itemValue}>{estimatedHours}</span>
      </div>
      <div className={css.lineItems}>
        {items.map(item => (
          <span key={item} className={css.itemLabel}>{item}</span>
        ))}
      </div>
      <div className={css.lineDivider} />
      <div className={css.lineItem}>
        <span className={css.itemLabel}>{customerCommission}% Marketplace Fees:</span>{' '}
        <span className={css.itemValue}>{transactionRole === 'provider' ? "-" : ""} ${formattedCommissionRate}</span>
      </div>
      <div className={css.lineDivider} />
      <div className={css.lineItem}>
        <span className={css.itemLabel}>
          <FormattedMessage id="BookingBreakdown.totalSum" values={{ transactionRole }} />
        </span>{' '}
        <span className={css.totalPrice}>${finalRate}</span>
      </div>

      {forProviderDeliveredByProvider ? (
        <div className={isMobileLayout ? css.mobileCustomerWait : css.customerWait}>
          <Button disabled={true}>
            <FormattedMessage id="TransactionPage.default-booking.provider.deliver-by-provider.actionButton" />
          </Button>
        </div>
      ) : null}
    </div>
  );
};

const OrderDetailsPanel = ({
  transactionState,
  protectedData,
  transactionRole,
  showQuote,
  setShowQuoteModal
}) => {
  const { customerQuoteRequest, proposedQuote, quote } = protectedData || {};

  switch (transactionState) {
    case states.INQUIRY:
    case states.INQUIRY_DECLINED:
    case states.INQUIRY_CANCELLED:
    case states.INQUIRE_EXPIRED:
      return <QuoteRequest
        customerQuoteRequest={customerQuoteRequest}
        showQuote={showQuote}
        setShowQuoteModal={setShowQuoteModal}
      />;

    case states.QUOTE_CREATED:
    case states.QUOTE_CANCELLED:
    case states.QUOTE_DECLINED:
    case states.QUOTE_EXPIRED:
    case states.PENDING_PAYMENT:
    case states.PURCHASED:
    case states.PAYMENT_EXPIRED:
    case states.PROVIDER_DELIVERED:
    case states.DELIVERED:
    case states.REVIEWED_BY_CUSTOMER:
    case states.REVIEWED_BY_PROVIDER:
    case states.REVIEWED:
      return showQuote ? <QuoteRequest
        customerQuoteRequest={customerQuoteRequest}
        isFinal={true}
        showQuote={showQuote}
        setShowQuoteModal={setShowQuoteModal}
        proposedQuote={proposedQuote ?? quote}
      /> : <FinalQuote
        proposedQuote={proposedQuote ?? quote}
        transactionRole={transactionRole}
        transactionState={transactionState}
        showQuote={showQuote}
        setShowQuoteModal={setShowQuoteModal}
      />;

    default:
      return null;
  }
};

export default OrderDetailsPanel;
